import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
  HTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  RefObject,
} from "react";
import "./Dialog.css";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Durations live in CSS. Reading the token back keeps the unmount timer and
 * the exit transition in step instead of duplicating the number in TS.
 */
function readDurationToken(token: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value)) return fallback;
  return raw.endsWith("ms") ? value : value * 1000;
}

/** Reference-counted through a body dataset so nested overlays cooperate. */
function lockScroll() {
  const next = Number(document.body.dataset["fcScrollLocks"] ?? "0") + 1;
  document.body.dataset["fcScrollLocks"] = String(next);
  if (next === 1) {
    document.body.dataset["fcScrollPrev"] = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}

function unlockScroll() {
  const next = Math.max(
    0,
    Number(document.body.dataset["fcScrollLocks"] ?? "1") - 1,
  );
  if (next === 0) {
    document.body.style.overflow = document.body.dataset["fcScrollPrev"] ?? "";
    delete document.body.dataset["fcScrollPrev"];
    delete document.body.dataset["fcScrollLocks"];
    return;
  }
  document.body.dataset["fcScrollLocks"] = String(next);
}

export interface DialogProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "title" | "children"
> {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Action row, bottom-right. Usually two `Button`s. */
  footer?: ReactNode;
  children?: ReactNode;
  /** `md` is 560px; `lg` (720px) is for settings-shaped content. */
  size?: "md" | "lg";
  /** Focus target on open. Defaults to the first focusable node. */
  initialFocus?: RefObject<HTMLElement | null>;
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  closeLabel?: string;
}

/**
 * A controlled modal, portalled to `document.body`. Traps focus, closes on
 * Escape and on backdrop click, restores focus to the opener, and locks body
 * scroll while open.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  size = "md",
  initialFocus,
  closeOnBackdropClick = true,
  showCloseButton = true,
  closeLabel = "Close",
  className,
  ...rest
}: DialogProps) {
  const uid = useId();
  const titleId = `${uid}title`;
  const descriptionId = `${uid}desc`;

  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Held in a ref so an inline `onClose` never re-runs the focus/lock effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  // Mount ahead of the enter transition, unmount behind the exit transition.
  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner = 0;
      const outer = window.requestAnimationFrame(() => {
        inner = window.requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        window.cancelAnimationFrame(outer);
        window.cancelAnimationFrame(inner);
      };
    }

    setVisible(false);
    const timer = window.setTimeout(
      () => setMounted(false),
      readDurationToken("--fc-duration-quick", 140),
    );
    return () => window.clearTimeout(timer);
  }, [open]);

  // Focus handover, scroll lock and Escape, all torn down on unmount.
  useEffect(() => {
    if (!mounted) return undefined;

    restoreRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    lockScroll();

    const panel = panelRef.current;
    const target =
      initialFocus?.current ??
      panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      panel;
    target?.focus();

    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onDocumentKeyDown);

    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
      unlockScroll();
      restoreRef.current?.focus();
    };
  }, [mounted, initialFocus]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (panel === null) return;

    const nodes = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );
    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    if (first === undefined || last === undefined) {
      event.preventDefault();
      panel.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!mounted || typeof document === "undefined") return null;

  const panelClasses = [
    "fc-dialog__panel",
    `fc-dialog__panel--${size}`,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return createPortal(
    <div
      className={visible ? "fc-dialog is-open" : "fc-dialog is-closing"}
      onKeyDown={handleKeyDown}
    >
      <div
        className="fc-dialog__backdrop"
        aria-hidden="true"
        onClick={closeOnBackdropClick ? onClose : undefined}
      />
      <div
        {...rest}
        ref={panelRef}
        className={panelClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
      >
        <div className="fc-dialog__header">
          <div className="fc-dialog__heading">
            <h2 className="fc-dialog__title" id={titleId}>
              {title}
            </h2>
            {description !== undefined ? (
              <p className="fc-dialog__description" id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          {showCloseButton ? (
            <button
              type="button"
              className="fc-dialog__close"
              aria-label={closeLabel}
              onClick={onClose}
            >
              <svg
                viewBox="0 0 16 16"
                width="16"
                height="16"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </div>

        {children !== undefined && children !== null ? (
          <div className="fc-dialog__body">{children}</div>
        ) : null}

        {footer !== undefined && footer !== null ? (
          <div className="fc-dialog__footer">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
